import Color from 'color';

/**
 * Checks whether a CSS color value should be allowed or stripped. Colors that are "dark enough
 * to be black" (luminosity <= 0.05) are rejected, because they interfere with dark mode and
 * are visually indistinguishable from the default text color in light mode.
 *
 * @param {string} cssColor - any value accepted by the `color` library (hex, rgb, named, etc.)
 * @returns {boolean} true when the color should be preserved
 */
export function shouldAllowElementColor(cssColor) {
  try {
    const color = Color(cssColor);
    return color.luminosity() > 0.05;
  } catch {
    // Unsupported value (e.g. `var(--some-variable)`) — strip it
    return false;
  }
}

/**
 * Checks whether a CSS color is too light to be visible on a white background. Colors with
 * luminosity >= 0.6 are rejected, because they would be nearly invisible in standard email
 * clients. Used when serializing outbound content to prevent dark mode theme colors from
 * leaking into external HTML.
 *
 * @param {string} cssColor - any value accepted by the `color` library (hex, rgb, named, etc.)
 * @returns {boolean} true when the color should be preserved
 */
export function shouldAllowExternalColor(cssColor) {
  try {
    const color = Color(cssColor);
    return color.luminosity() < 0.6;
  } catch {
    return false;
  }
}

/**
 * Checks whether a value is a well-formed CSS color, so it can be safely interpolated into a
 * `style` attribute. We serialize colors read off pasted HTML into `style="color:..."` /
 * `style="background-color:..."`; a value the `color` library can parse cannot contain the `"`,
 * `;`, `<` or `>` needed to break out of the attribute (verified: it throws on those). Values come
 * from the browser's normalized `style` getters today, but this keeps the serializer safe regardless.
 *
 * @param {string} cssColor - candidate color value (hex, rgb, named, etc.)
 * @returns {boolean} true when the value is a safe, well-formed CSS color
 */
export function isValidCssColor(cssColor) {
  try {
    Color(cssColor);
    return true;
  } catch {
    return false;
  }
}
